import React, { useState, useEffect } from 'react';
import { SystemConfig, School } from '../../data/types';
import { dataService } from '../../services/dataService';

interface SkipAndMoveTabProps {
  config: SystemConfig;
  onSave: (updatedConfig: Partial<SystemConfig>) => void;
  saving: boolean;
}

const SkipAndMoveTab: React.FC<SkipAndMoveTabProps> = ({ config, onSave, saving }) => {
  const [enabled, setEnabled] = useState(config.skipAndMoveToGamePolicy.enabled);
  const [districtDefault, setDistrictDefault] = useState(config.skipAndMoveToGamePolicy.districtDefault);
  const [schoolOverrides, setSchoolOverrides] = useState(config.skipAndMoveToGamePolicy.schoolOverrides);
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        setLoading(true);
        const schoolsData = await dataService.getSchools();
        setSchools(schoolsData);
      } catch (error) {
        console.error('Error fetching schools:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  const handleSave = () => {
    const updatedConfig: Partial<SystemConfig> = {
      skipAndMoveToGamePolicy: {
        enabled,
        districtDefault,
        schoolOverrides
      }
    };
    
    onSave(updatedConfig);
  };

  const handleSchoolOverrideChange = (schoolId: string, value: boolean) => {
    setSchoolOverrides({
      ...schoolOverrides,
      [schoolId]: value
    });
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">Skip and Move to Game Policy</h2>
      <p className="text-gray-600 mb-6">
        Configure whether students can skip instructional content and practice questions 
        to move directly to games. This can be set district-wide or customized per school.
      </p>
      
      {/* Global Toggle */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-700">Feature Status</h3>
            <p className="text-sm text-gray-500">Enable or disable the Skip and Move to Game feature district-wide</p>
          </div>
          <div className="flex items-center">
            <span className="mr-3 text-sm text-gray-500">{enabled ? 'Enabled' : 'Disabled'}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={enabled}
                onChange={() => setEnabled(!enabled)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>
      
      {enabled && (
        <>
          {/* District Default */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">District Default</h3>
            <p className="text-sm text-gray-500 mb-3">
              Set the default policy for all schools that don't have a specific override
            </p>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="districtDefault"
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                checked={districtDefault}
                onChange={() => setDistrictDefault(!districtDefault)}
              />
              <label htmlFor="districtDefault" className="ml-2 block text-sm text-gray-700">
                Enable Skip and Move to Game by default
              </label>
            </div>
          </div>
          
          {/* School Overrides */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-3">School-Specific Overrides</h3>
            <p className="text-sm text-gray-500 mb-3">
              Configure custom settings for specific schools that differ from the district default
            </p>
            
            {loading ? (
              <div className="flex items-center justify-center h-20">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">School</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Override</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {schools.map((school) => {
                      const hasOverride = school.id in schoolOverrides;
                      const isEnabled = hasOverride ? schoolOverrides[school.id] : districtDefault;
                      
                      return (
                        <tr key={school.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              {school.logo ? (
                                <img src={school.logo} alt={school.name} className="h-8 w-8 rounded-full mr-2" />
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                                  <span className="text-gray-600 text-xs font-bold">{school.name.substring(0, 2).toUpperCase()}</span>
                                </div>
                              )}
                              <div className="text-sm font-medium text-gray-900">{school.name}</div>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`override-${school.id}`}
                                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                checked={hasOverride}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    // Add override with current district default value
                                    handleSchoolOverrideChange(school.id, districtDefault);
                                  } else {
                                    // Remove override
                                    const newOverrides = { ...schoolOverrides };
                                    delete newOverrides[school.id];
                                    setSchoolOverrides(newOverrides);
                                  }
                                }}
                              />
                              <label htmlFor={`override-${school.id}`} className="ml-2 block text-sm text-gray-700">
                                Custom Setting
                              </label>
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {hasOverride ? (
                              <div className="flex items-center">
                                <span className="mr-3 text-sm text-gray-500">{isEnabled ? 'Enabled' : 'Disabled'}</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                  <input 
                                    type="checkbox" 
                                    className="sr-only peer" 
                                    checked={isEnabled}
                                    onChange={() => handleSchoolOverrideChange(school.id, !isEnabled)}
                                    disabled={!hasOverride}
                                  />
                                  <div className={`w-11 h-6 ${hasOverride ? 'bg-gray-200' : 'bg-gray-100'} peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary ${!hasOverride && 'opacity-50 cursor-not-allowed'}`}></div>
                                </label>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-500">
                                Using district default ({districtDefault ? 'Enabled' : 'Disabled'})
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Save Button */}
      <div className="flex justify-end">
        <button
          className={`px-4 py-2 rounded text-white ${
            saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark'
          }`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Policy Settings'}
        </button>
      </div>
    </div>
  );
};

export default SkipAndMoveTab;
